import { type ChargerVendor, chargerVendors } from './chargerFamilies';
import { ChargingStation, chargingStationTypeKey, displayChargingStationType } from './model';

/**
 * Which stations can share one type-restricted bulk action (a firmware image serves one
 * group). `key` is opaque and only compared; `label` is what the user sees, empty when
 * the station has no known type.
 */
export interface StationGroupIdentity {
  key: string;
  label: string;
}

export interface StationGroup extends StationGroupIdentity {
  stationIds: string[];
}

export type StationGroupResolver = (station: Pick<ChargingStation, 'type'>) => StationGroupIdentity;

/** One group per exact vendor and model: the fallback for hardware outside the charger catalogue. */
export const vendorModelGroup: StationGroupResolver = (station) => ({
  key: chargingStationTypeKey(station),
  label: station.type ? [station.type.vendor, displayChargingStationType(station.type)].filter(Boolean).join(' / ') : ''
});

/**
 * Groups by charger family where a vendor's model matches one, and falls back to `fallback` for
 * everything else, so an unknown vendor is never merged with another one.
 */
export function createChargerFamilyResolver(
  vendors: ChargerVendor[],
  fallback: StationGroupResolver = vendorModelGroup
): StationGroupResolver {
  return (station) => {
    const vendorName = station.type?.vendor ?? '';
    const model = station.type?.model ?? '';
    const family = vendors
      .find((vendor) => vendor.vendorPattern.test(vendorName))
      ?.families.find((candidate) => candidate.modelPrefixes.some((prefix) => model.startsWith(prefix)));
    return family ? { key: family.key, label: family.label } : fallback(station);
  };
}

export const defaultStationGroupResolver = createChargerFamilyResolver(chargerVendors);

let activeResolver: StationGroupResolver = defaultStationGroupResolver;

/** Called once at app boot by an app that groups differently than the charger catalogue. */
export function setStationGroupResolver(resolver: StationGroupResolver): void {
  activeResolver = resolver;
}

export function resolveStationGroup(station: Pick<ChargingStation, 'type'>): StationGroupIdentity {
  return activeResolver(station);
}

export interface StationGrouper {
  add: (station: Pick<ChargingStation, 'id' | 'type'>) => void;
  groups: () => StationGroup[];
}

/**
 * Accumulates stations into groups, so a caller that pages through thousands of stations
 * never has to hold them all.
 */
export function createStationGrouper(): StationGrouper {
  // Keyed on `key`, never `label`: labels are display text and two groups may share one.
  const byKey = new Map<string, StationGroup>();

  function add(station: Pick<ChargingStation, 'id' | 'type'>): void {
    const identity = resolveStationGroup(station);
    const group = byKey.get(identity.key);
    if (group) {
      group.stationIds.push(station.id);
      return;
    }
    byKey.set(identity.key, { ...identity, stationIds: [station.id] });
  }

  function groups(): StationGroup[] {
    return [...byKey.values()]
      .map((group) => ({ ...group, stationIds: [...group.stationIds] }))
      .sort((a, b) => b.stationIds.length - a.stationIds.length || a.label.localeCompare(b.label));
  }

  return { add, groups };
}

export function groupStations(stations: Pick<ChargingStation, 'id' | 'type'>[]): StationGroup[] {
  const grouper = createStationGrouper();
  stations.forEach((station) => grouper.add(station));
  return grouper.groups();
}
